import type { ReactElement } from 'react';
import React from 'react';
import { Outlet } from 'react-router-dom';
import { EmbedViewModeProvider } from '../views/providers/EmbedViewModeProvider';

export const EMBED_VIEW_IDS = [
  'profit-and-loss',
  'guest-balances',
  'trust-reconciliation',
  'trust-reconciliation-by-listing',
  'trial-balance',
  'sales-tax-liability',
  'balance-sheet',
  'statement-summaries',
  'detail',
] as const;

export type EmbedViewId = (typeof EMBED_VIEW_IDS)[number];

export type EmbedViewQueryParamNote = {
  description: string;
  name: string;
  public: boolean;
  required?: boolean;
};

export type EmbedViewDefinition = {
  description: string;
  disableAutoStory?: boolean;
  docsCategory: 'Reports';
  examplePath: string;
  id: EmbedViewId;
  label: string;
  queryParamNotes?: EmbedViewQueryParamNote[];
  render: () => ReactElement;
  routePath: string;
  routePattern: string;
  storybookPreviewHeight: number;
};

const createPublicQueryParamNote = (
  name: string,
  description: string,
  options?: { required?: boolean }
): EmbedViewQueryParamNote => ({
  name,
  description,
  public: true,
  required: options?.required,
});

type EmbedViewModule = typeof import('../views');
type EmbedViewLoader = () => Promise<{
  default: React.ComponentType;
}>;

const withEmbedViewMode = (element: ReactElement) =>
  React.createElement(
    EmbedViewModeProvider,
    {
      value: { isEmbed: true },
    } as React.ComponentProps<typeof EmbedViewModeProvider>,
    element
  );

const renderLazyEmbedView = (loadView: EmbedViewLoader) =>
  withEmbedViewMode(
    React.createElement(
      React.Suspense,
      null,
      React.createElement(React.lazy(loadView))
    )
  );

const loadViewModule = () => import('../views') as Promise<EmbedViewModule>;

const createNamedViewLoader = <T extends React.ComponentType>(
  selectView: (module: EmbedViewModule) => T
): EmbedViewLoader => {
  return async () => {
    const module = await loadViewModule();

    return {
      default: selectView(module),
    };
  };
};

const renderBalanceSheetView = () =>
  renderLazyEmbedView(async () => {
    const module = await loadViewModule();

    return {
      default: () =>
        React.createElement(
          module.BalanceSheetRoutes,
          null,
          React.createElement(Outlet, null)
        ),
    };
  });

export const EMBED_VIEW_DEFINITIONS: readonly EmbedViewDefinition[] = [
  {
    id: 'profit-and-loss',
    label: 'P&L',
    description:
      'Profit and loss report with the default and filtered row variants.',
    docsCategory: 'Reports',
    routePattern: '/reports/profit-and-loss/*',
    routePath: '/reports/profit-and-loss/*',
    examplePath: '/reports/profit-and-loss?view=non-zero-rows',
    queryParamNotes: [
      createPublicQueryParamNote(
        'date',
        '`YYYY-MM-DD...YYYY-MM-DD` range applied on mount.'
      ),
      createPublicQueryParamNote(
        'listingIds',
        'Comma-separated listing IDs to prefilter the report.'
      ),
      createPublicQueryParamNote('party', '`owners` or `manager` party scope.'),
      createPublicQueryParamNote(
        'ledger',
        '`trust` or `operating` ledger scope.'
      ),
      createPublicQueryParamNote(
        'view',
        'Comma-separated `non-zero-rows` and/or `non-zero-columns` flags.'
      ),
    ],
    storybookPreviewHeight: 760,
    render: () =>
      renderLazyEmbedView(
        createNamedViewLoader((module) => module.ProfitAndLossRoutes)
      ),
  },
  {
    id: 'guest-balances',
    label: 'Guests',
    description: 'Guest balances report with occupancy-status route variants.',
    docsCategory: 'Reports',
    routePattern: '/reports/guest-balances/*',
    routePath: '/reports/guest-balances/*',
    examplePath: '/reports/guest-balances',
    queryParamNotes: [
      createPublicQueryParamNote(
        'endDate',
        '`YYYY-MM-DD` as-of date applied on mount.'
      ),
      createPublicQueryParamNote(
        'channels',
        'Comma-separated booking channel values.'
      ),
    ],
    storybookPreviewHeight: 760,
    render: () =>
      renderLazyEmbedView(
        createNamedViewLoader((module) => module.GuestBalancesRoutes)
      ),
  },
  {
    id: 'trust-reconciliation',
    label: 'Trust',
    description: 'Trust reconciliation summary table.',
    docsCategory: 'Reports',
    routePattern: '/reports/trust-reconciliation/*',
    routePath: '/reports/trust-reconciliation/*',
    examplePath: '/reports/trust-reconciliation',
    queryParamNotes: [
      createPublicQueryParamNote(
        'listingId',
        'Specific listing ID to prefilter the reconciliation rows.'
      ),
      createPublicQueryParamNote(
        'endDate',
        '`YYYY-MM-DD` as-of date applied on mount.'
      ),
    ],
    storybookPreviewHeight: 760,
    render: () =>
      renderLazyEmbedView(
        createNamedViewLoader((module) => module.TrustReconcilationTable)
      ),
  },
  {
    id: 'trust-reconciliation-by-listing',
    label: 'By Listing',
    description: 'Trust reconciliation broken out by listing.',
    docsCategory: 'Reports',
    routePattern: '/reports/trust-reconciliation-by-listing/*',
    routePath: '/reports/trust-reconciliation-by-listing/*',
    examplePath: '/reports/trust-reconciliation-by-listing',
    queryParamNotes: [
      createPublicQueryParamNote(
        'endDate',
        '`YYYY-MM-DD` as-of date applied on mount.'
      ),
    ],
    storybookPreviewHeight: 760,
    render: () =>
      renderLazyEmbedView(
        createNamedViewLoader(
          (module) => module.TrustReconciliationByListingTable
        )
      ),
  },
  {
    id: 'trial-balance',
    label: 'Trial',
    description: 'Trial balance table view.',
    docsCategory: 'Reports',
    routePattern: '/reports/trial-balance/*',
    routePath: '/reports/trial-balance/*',
    examplePath: '/reports/trial-balance',
    queryParamNotes: [
      createPublicQueryParamNote(
        'search',
        'Free-text account search applied on mount.'
      ),
      createPublicQueryParamNote(
        'date',
        '`YYYY-MM-DD...YYYY-MM-DD` range applied on mount.'
      ),
      createPublicQueryParamNote(
        'classifications',
        'Comma-separated `asset`, `liability`, `revenue`, or `expense` values.'
      ),
      createPublicQueryParamNote('party', '`owners` or `manager` party scope.'),
      createPublicQueryParamNote(
        'type',
        '`bank`, `ledger`, `recurringFee`, or `nonPosting` account type.'
      ),
      createPublicQueryParamNote(
        'ledger',
        '`trust` or `operating` ledger scope.'
      ),
      createPublicQueryParamNote(
        'view',
        '`non-zero` to hide zero-balance rows on mount.'
      ),
    ],
    storybookPreviewHeight: 760,
    render: () =>
      renderLazyEmbedView(
        createNamedViewLoader((module) => module.TrialBalanceTable)
      ),
  },
  {
    id: 'sales-tax-liability',
    label: 'Sales Tax',
    description: 'Sales tax liability table view.',
    docsCategory: 'Reports',
    routePattern: '/reports/sales-tax-liability/*',
    routePath: '/reports/sales-tax-liability/*',
    examplePath: '/reports/sales-tax-liability',
    queryParamNotes: [
      createPublicQueryParamNote(
        'date',
        '`YYYY-MM-DD...YYYY-MM-DD` range applied on mount.'
      ),
      createPublicQueryParamNote(
        'channels',
        'Comma-separated booking channel values.'
      ),
    ],
    storybookPreviewHeight: 760,
    render: () =>
      renderLazyEmbedView(
        createNamedViewLoader((module) => module.SalesTaxLiabilityTable)
      ),
  },
  {
    id: 'balance-sheet',
    label: 'Balance',
    description: 'Balance sheet report with row filter variants.',
    docsCategory: 'Reports',
    routePattern: '/reports/balance-sheet/*',
    routePath: '/reports/balance-sheet/*',
    examplePath: '/reports/balance-sheet?view=non-zero-rows',
    queryParamNotes: [
      createPublicQueryParamNote(
        'date',
        '`YYYY-MM-DD...YYYY-MM-DD` range applied on mount.'
      ),
      createPublicQueryParamNote(
        'ledger',
        '`trust` or `operating` ledger scope.'
      ),
      createPublicQueryParamNote(
        'view',
        'Comma-separated `non-zero-rows` and/or `non-zero-columns` flags.'
      ),
    ],
    storybookPreviewHeight: 760,
    render: renderBalanceSheetView,
  },
  {
    id: 'statement-summaries',
    label: 'Statements',
    description:
      'Owner statement summaries list and detail routes, including redirects.',
    docsCategory: 'Reports',
    routePattern: '/reports/statement-summaries/*',
    routePath: '/reports/statement-summaries/*',
    examplePath: '/reports/statement-summaries',
    queryParamNotes: [
      createPublicQueryParamNote(
        'view',
        'Detail route only: `listing`, `month`, `reservation`, or `bookingChannel`.'
      ),
      createPublicQueryParamNote(
        'listingIds',
        'Detail route only: comma-separated listing IDs.'
      ),
    ],
    storybookPreviewHeight: 900,
    render: () =>
      renderLazyEmbedView(
        createNamedViewLoader((module) => module.OwnerStatementSummariesRoutes)
      ),
  },
  {
    id: 'detail',
    label: 'Detail',
    description: 'Journal entry detail table for drilldown flows.',
    docsCategory: 'Reports',
    routePattern: '/reports/detail/*',
    routePath: '/reports/detail/*',
    examplePath: '/reports/detail',
    queryParamNotes: [
      createPublicQueryParamNote(
        'search',
        'Free-text search applied on mount.'
      ),
      createPublicQueryParamNote(
        'amount',
        'Amount filter in cents: `eq12345`, `gt12345`, `lt12345`, or `10000...20000`.'
      ),
      createPublicQueryParamNote(
        'listingIds',
        'Comma-separated listing IDs. Supports `unmapped`.'
      ),
      createPublicQueryParamNote(
        'listingOwnershipPeriodIds',
        'Comma-separated listing ownership period IDs.'
      ),
      createPublicQueryParamNote('contactIds', 'Comma-separated contact IDs.'),
      createPublicQueryParamNote(
        'bookingChannels',
        'Comma-separated booking channel values.'
      ),
      createPublicQueryParamNote(
        'reservationIds',
        'Comma-separated reservation IDs.'
      ),
      createPublicQueryParamNote('party', '`owners` or `manager` party scope.'),
      createPublicQueryParamNote(
        'type',
        '`reservation`, `deposit`, `expense`, `recurringFee`, or `transfer`.'
      ),
      createPublicQueryParamNote(
        'subType',
        'Comma-separated journal entry subtype keys such as `transaction_expense_line`.'
      ),
      createPublicQueryParamNote(
        'date',
        '`YYYY-MM-DD` or `YYYY-MM-DD...YYYY-MM-DD` applied on mount.'
      ),
      createPublicQueryParamNote(
        'published',
        '`draft` or `published` owner-statement status.'
      ),
      createPublicQueryParamNote('accountIds', 'Comma-separated account IDs.'),
      createPublicQueryParamNote(
        'classifications',
        'Comma-separated `asset`, `liability`, `revenue`, or `expense` values.'
      ),
      createPublicQueryParamNote(
        'currency',
        'Currency code to prefilter matching journal entries.'
      ),
      createPublicQueryParamNote(
        'ledger',
        '`trust` or `operating` ledger scope.'
      ),
      createPublicQueryParamNote('status', '`active`, `inactive`, or `all`.'),
      createPublicQueryParamNote(
        'openingBalanceType',
        '`taxPayable`, `advancedDeposit`, or `listingOpeningBalance`.'
      ),
    ],
    storybookPreviewHeight: 760,
    render: () =>
      renderLazyEmbedView(
        createNamedViewLoader((module) => module.JournalEntriesView)
      ),
  },
];

export const DEFAULT_EMBED_VIEW_ID: EmbedViewId = EMBED_VIEW_DEFINITIONS[0].id;

export const getEmbedViewById = (viewId: EmbedViewId) =>
  EMBED_VIEW_DEFINITIONS.find((view) => view.id === viewId) ??
  EMBED_VIEW_DEFINITIONS[0];

export const getAutoStoryEmbedViews = () =>
  EMBED_VIEW_DEFINITIONS.filter((view) => !view.disableAutoStory);
